import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { SALES_REVENUE_MONTHLY } from '../../consts/reports';
import { SALES_REVENUE_MONTHLY_TYPE } from '../../consts/cronTypes';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { toast } from 'react-toastify';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const DEFAULT_END_DATE = new Date().toISOString().split('T')[0];
const DEFAULT_START_DATE = (() => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date.toISOString().split('T')[0];
})();

// Helper to convert "YYYY-MM" to "Month YYYY" (e.g., "2025-01" -> "January 2025")
const formatMonthLabel = (ym) => {
  const [year, month] = ym.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
};

const SalesRevenueMonthlyReport = ({
  marginFromSidebar
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['sales-revenue-monthly'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['sales-revenue-monthly'] || [];

  // State management
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  // Date filter state
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  const [months, setMonths] = useState([]);

  useEffect(() => {
    fetchReportMetadata();
    dispatch(getReportEmailSettings('sales-revenue-monthly'));
    dispatch(getReportSendHistory('sales-revenue-monthly'));
  }, [dispatch]);

  useEffect(() => {
    if (!initialReady) return;
    if (startDate && endDate) {
      fetchReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReady, startDate, endDate, currentPage, sortField, sortDirection]);

  const fetchReportMetadata = async () => {
    try {
      const response = await fetch(`/api/v1/reports/${SALES_REVENUE_MONTHLY}/metadata`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch report metadata');
      const data = await response.json();
      setColumns(data.columns || []);
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError('Failed to load report configuration');
    }
  };

  const fetchReportData = async () => {
    if (!startDate || !endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      });
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      const response = await fetch(`/api/v1/reports/${SALES_REVENUE_MONTHLY}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch report data');
      }
      const data = await response.json();
      if (!isLatest(requestId)) return;
      const rows = data.data || [];
      // Determine unique months
      const monthSet = new Set(rows.map(r => r.month));
      const months = Array.from(monthSet).sort();

      // Pivot rows by product
      const productMap = new Map();
      rows.forEach(r => {
        if (!productMap.has(r.product)) {
          productMap.set(r.product, { product: r.product });
        }
        const prodRow = productMap.get(r.product);
        prodRow[r.month] = r.revenue_percentage;
      });

      const pivotData = Array.from(productMap.values());

      // Build columns: Product + months
      const dynamicColumns = [
        { 
          key: 'product', 
          label: 'Product', 
          sortable: true,
          headerClassName: 'w-48 min-w-[12rem]', // Fixed width for product column
          sticky: true
        }
      ];
      months.forEach(m => {
        dynamicColumns.push({
          key: m,
          label: (
            <span className="block w-32 min-w-[8rem] text-right">{formatMonthLabel(m)}</span>
          ),
          sortable: true,
          headerClassName: 'w-32 min-w-[8rem]' // Fixed width for month columns
        });
      });

      setColumns(dynamicColumns);
      setReportData(pivotData);
      setTotalRecords(pivotData.length);
      setTotalPages(1);
      setMonths(months);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  };

  const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
  };

  const handleSort = ({ field, direction }) => {
    // client-side sorting on pivoted data
    setSortField(field);
    setSortDirection(direction);

    setReportData(prevData => {
      const sorted = [...prevData].sort((a, b) => {
        if (field === 'product') {
          return direction === 'asc' ? a.product.localeCompare(b.product) : b.product.localeCompare(a.product);
        }
        // month columns: compare numeric percentage (fallback 0)
        const valA = parseFloat((a[field] || '0').replace('%', ''));
        const valB = parseFloat((b[field] || '0').replace('%', ''));
        return direction === 'asc' ? valA - valB : valB - valA;
      });
      return sorted;
    });
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleExportCSV = async () => {
    if (!startDate || !endDate) throw new Error('Please select a date range first');
    const params = new URLSearchParams({ startDate, endDate });
    if (sortField) {
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);
    }
    const response = await fetch(`/api/v1/reports/${SALES_REVENUE_MONTHLY}/export?${params}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') : `sales-revenue-monthly_${new Date().toISOString().slice(0,10)}.csv`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' }
  ];

  const filtersComponent = (
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      onDateChange={handleDateChange}
      label="Date Range"
      error={error && error.includes('date') ? error : null}
    />
  );

  const exportButton = (
    <CSVExportButton
      data={[]}
      columns={columns}
      filename={`${SALES_REVENUE_MONTHLY}`}
      onExport={handleExportCSV}
      disabled={!startDate || !endDate || isLoading}
      variant="outline"
    />
  );

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth || null,
      slackChannelId: formData?.slackChannelId || null,
      dayOfWeek: formData?.dayOfWeek || null,
      timeOfDay: formData?.timeOfDay || null,
      emailMessage: formData?.emailMessage || null,
    };
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'sales-revenue-monthly', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType || SALES_REVENUE_MONTHLY_TYPE,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'sales-revenue-monthly', ...payload })).unwrap();
      await dispatch(getReportSendHistory('sales-revenue-monthly')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'sales-revenue-monthly', ...payload })).unwrap();
      await dispatch(getReportSendHistory('sales-revenue-monthly')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const paginationComponent = null;

  const renderRows = () => reportData.map((row, idx) => (
    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-48 min-w-[12rem] ${idx % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>{row.product}</td>
      {months.map((m, ix) => (
        <td key={ix} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right w-32 min-w-[8rem]">{row[m] || '-'}</td>
      ))}
    </tr>
  ));

  return (
    <>
      <ReportTemplate
        reportId={SALES_REVENUE_MONTHLY}
        description="Shows, for each month, the percentage of total revenue contributed by every active product (Shopify + Amazon)"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={
          <div className="flex items-center space-x-2">
            {exportButton}
            <div>
              <Cog8ToothIcon
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={() => {
                  setIsOpenSettings(true);
                }}
              />
            </div>
          </div>
        }
        data={reportData}
        cronType={emailSettings?.cronType || null}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No data for selected date range."
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {renderRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'sales-revenue-monthly'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="sales-revenue-monthly"
      />
    </>
  );
};

export default SalesRevenueMonthlyReport; 